import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bot,
  Headphones,
  Inbox,
  Megaphone,
  MessageCircle,
  Target,
  Users,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ContactAgentMetricCard } from '@/features/contact-agent/components/ContactAgentMetricCard';
import type { DashboardSnapshot } from './useDashboardSnapshot';

type QuickLink = {
  to: string;
  label: string;
  description: string;
  icon: typeof Bot;
};

type DashboardModulesProps = {
  flags: {
    chatbot: boolean;
    contactAgent: boolean;
    mesa: boolean;
    whatsApp: boolean;
    inbox: boolean;
    leads: boolean;
  };
  isOperator: boolean;
  snapshot: DashboardSnapshot;
  loading: boolean;
};

function buildQuickLinks(flags: DashboardModulesProps['flags'], isOperator: boolean): QuickLink[] {
  const links: QuickLink[] = [];

  if (isOperator || flags.inbox) {
    links.push({
      to: '/admin/inbox',
      label: 'Inbox',
      description: 'Conversaciones que requieren atención',
      icon: Inbox,
    });
  }

  if (flags.chatbot && !isOperator) {
    links.push({
      to: '/admin/chatbot',
      label: 'Chatbots',
      description: 'Instancias, bots y configuración',
      icon: Bot,
    });
  }

  if (flags.contactAgent && !isOperator) {
    links.push({
      to: '/admin/campaigns',
      label: 'Campañas',
      description: 'Cobranzas, avisos y seguimiento',
      icon: Megaphone,
    });
    links.push({
      to: '/admin/contact-agent/work-queue',
      label: 'Bandeja de trabajo',
      description: 'Casos activos del operador',
      icon: Users,
    });
  }

  if (flags.mesa && !isOperator) {
    links.push({
      to: '/admin/mesa',
      label: 'Mesa de ayuda',
      description: 'Agentes y tickets de soporte',
      icon: Headphones,
    });
  }

  if (flags.whatsApp && !isOperator) {
    links.push({
      to: '/admin/whatsapp',
      label: 'WhatsApp',
      description: 'Canales y plantillas',
      icon: MessageCircle,
    });
  }

  if (flags.leads && !isOperator) {
    links.push({
      to: '/admin/leads',
      label: 'Leads',
      description: 'Embudo comercial',
      icon: Target,
    });
  }

  return links.slice(0, 6);
}

export function DashboardModules({ flags, isOperator, snapshot, loading }: DashboardModulesProps) {
  const quickLinks = buildQuickLinks(flags, isOperator);
  const mesaOpen = snapshot.mesa ? snapshot.mesa.open + snapshot.mesa.inProgress : null;

  return (
    <div className='space-y-6'>
      <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-3'>
        {flags.chatbot && !isOperator ? (
          <ContactAgentMetricCard
            title='Instancias chatbot'
            value={loading && snapshot.chatbotInstances === null ? '…' : String(snapshot.chatbotInstances ?? '—')}
            hint='instancias activas del tenant'
          />
        ) : null}

        {flags.contactAgent && !isOperator && snapshot.contactAgent ? (
          <>
            <ContactAgentMetricCard
              title='Conversaciones iniciadas'
              value={String(snapshot.contactAgent.conversationsStarted)}
              hint='campañas de contact-agent'
            />
            <ContactAgentMetricCard
              title='Conversaciones respondidas'
              value={String(snapshot.contactAgent.conversationsResponded)}
              hint={`${snapshot.contactAgent.conversationsNoResponse} sin respuesta`}
            />
          </>
        ) : null}

        {flags.mesa && !isOperator && snapshot.mesa ? (
          <ContactAgentMetricCard
            title='Tickets abiertos'
            value={String(mesaOpen ?? 0)}
            hint={`${snapshot.mesa.resolved} resueltos · ${snapshot.mesa.closed} cerrados`}
          />
        ) : null}

        {isOperator ? (
          <ContactAgentMetricCard
            title='Tu bandeja'
            value='Inbox'
            hint='Atiende conversaciones asignadas desde el inbox'
          />
        ) : null}
      </div>

      {quickLinks.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Accesos rápidos</CardTitle>
          </CardHeader>
          <CardContent className='grid gap-3 sm:grid-cols-2 xl:grid-cols-3'>
            {quickLinks.map(link => {
              const Icon = link.icon;
              return (
                <Button key={link.to} variant='outline' className='h-auto justify-start p-4' asChild>
                  <Link to={link.to}>
                    <span className='flex w-full items-start gap-3 text-left'>
                      <Icon className='mt-0.5 h-5 w-5 shrink-0 text-primary' />
                      <span className='space-y-1'>
                        <span className='block font-medium'>{link.label}</span>
                        <span className='block text-xs text-muted-foreground'>{link.description}</span>
                      </span>
                      <ArrowRight className='ml-auto h-4 w-4 text-muted-foreground' />
                    </span>
                  </Link>
                </Button>
              );
            })}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
